import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, Info, X, ChevronDown, BookOpen, GitCompare } from "lucide-react";
import { TooltipProvider } from "../components/ui/tooltip";
import { RefetchPill } from "../components/Loader";
import useMinVisible from "../hooks/useMinVisible";
import { usePersistentState } from "../hooks/usePersistentState";
import { useDebounced } from "../hooks/useDebounced";
import PdfExportButton from "../components/PdfExportButton";
import CampaignCompareModal from "../components/scorecard/CampaignCompareModal";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

import FilterBar from "../components/scorecard/FilterBar";
import ScorecardSkeleton from "../components/scorecard/ScorecardSkeleton";
import OverviewRibbon from "../components/scorecard/OverviewRibbon";
import TicketPromedioMini from "../components/scorecard/TicketPromedioMini";
import ChannelPieChart from "../components/scorecard/ChannelPieChart";
import SalesPieChart from "../components/scorecard/SalesPieChart";
import CampaignTable from "../components/scorecard/CampaignTable";
import TimeseriesChart from "../components/scorecard/TimeseriesChart";
import AttributionAuditSection from "../components/scorecard/AttributionAuditSection";
import WebSubsourcesPanel from "../components/scorecard/WebSubsourcesPanel";
import ResponseTimeCard from "../components/ResponseTimeCard";
import ConversionScoreCard from "../components/scorecard/ConversionScoreCard";
import AttributionRateCard from "../components/scorecard/AttributionRateCard";
import DigitalPropertyScoreCard from "../components/scorecard/DigitalPropertyScoreCard";
import DataFreshnessBadge from "../components/scorecard/DataFreshnessBadge";
import CacheBustButton from "../components/scorecard/CacheBustButton";
import { isoToday } from "../components/scorecard/formatters";

/**
 * Scorecard ND · KPIs Comerciales (v3)
 *  - Filtros: marca · modelo (dependiente) · sucursal · unidad · canal (click en pie)
 *  - Rango de fechas: presets (Mes / Trimestre / YTD) o rango personalizado
 *  - Overview ribbon con 7 métricas + tooltips explicativos por métrica
 *  - Timeseries horizontal full-width con callouts mensuales visibles
 *
 * All child components live under `../components/scorecard/*` — this page
 * is intentionally kept as a pure orchestrator (state + composition only).
 */
export default function ScorecardPage() {
  const { user } = useAuth();
  const pageRef = useRef(null);
  const [data, setData]           = useState(null);
  const [options, setOptions]     = useState({ brands: [], models: [], branches: [], channels: [], business_units: [], campaigns: [] });
  const [loading, setLoading]     = useState(true);
  const showRefetchPill = useMinVisible(loading, 700);
  // Persist period + filters + custom date range so F5 / navigation
  // doesn't wipe the analyst's context. Namespaced per page.
  const [period, setPeriod, clearPeriod] = usePersistentState("scorecard:period", "year_to_date");
  const [dateRange, setDateRange, clearDateRange] = usePersistentState(
    "scorecard:date-range",
    { start: `${new Date().getFullYear()}-01-01`, end: isoToday() },
  );
  // Bumped to v2 when the `campaign` facet was added (2026-02-12) so any
  // localStorage from v1 without the field is re-hydrated with the new
  // shape merged in via the shallow-merge in usePersistentState.
  const [filters, setFilters, clearFilters] = usePersistentState(
    "scorecard:filters",
    { brand: [], model: [], branch: [], business_unit: [], channel: [], campaign: [] },
    { version: 2 },
  );
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  // Debounce filter-driven refetches so a burst of multi-select clicks
  // (typical when picking 4-5 brands at once) collapses into a single
  // request instead of 5.
  const debouncedFilters = useDebounced(filters, 300);
  const debouncedDateRange = useDebounced(dateRange, 350);

  // Load main scorecard payload — depends on every filter that can shift
  // the result set (period + custom date range + facet filters + channel).
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const params = { year: 2026, period };
        if (period === "custom") {
          params.start_date = debouncedDateRange.start;
          params.end_date   = debouncedDateRange.end;
        }
        if (selectedChannel) params.channel = selectedChannel;
        Object.entries(debouncedFilters).forEach(([k, v]) => { if (v && v.length) params[k] = v; });
        const r = await api.get("/scorecard/commercial", {
          params,
          // Multi-value params serialized as `?brand=X&brand=Y` (FastAPI's
          // expected format for `List[str]` query dependencies).
          paramsSerializer: { indexes: null },
        });
        if (mounted) setData(r.data);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [period, debouncedDateRange.start, debouncedDateRange.end, selectedChannel,
      debouncedFilters.brand, debouncedFilters.model, debouncedFilters.branch,
      debouncedFilters.business_unit, debouncedFilters.channel, debouncedFilters.campaign]);

  // Load filter options (brand-dependent — models refetch when brand changes).
  useEffect(() => {
    let mounted = true;
    (async () => {
      const r = await api.get("/scorecard/filter-options", {
        params: debouncedFilters.brand.length ? { brand: debouncedFilters.brand } : {},
        paramsSerializer: { indexes: null },
      });
      if (mounted) setOptions(r.data);
    })();
    return () => { mounted = false; };
  }, [debouncedFilters.brand]);

  // Reset dependent filters on parent change to prevent orphan selections.
  useEffect(() => { setFilters((f) => ({ ...f, model: [] })); }, [filters.brand]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSelectedChannel(null); },
    [period, filters.brand, filters.model, filters.branch, filters.business_unit, filters.channel, filters.campaign]);

  const resetAll = () => {
    clearPeriod();
    clearDateRange();
    clearFilters();
    setSelectedChannel(null);
  };

  const filteredCampaigns = selectedChannel
    ? (data?.campaigns_conversion || []).filter((c) => c.channel === selectedChannel)
    : (data?.campaigns_conversion || []);

  // Trim model pie to top 5 + "Otros" so the card matches the height of
  // the other two pies while preserving the true unit total.
  const modelPieData = (() => {
    const src = data?.sales_by_model || [];
    if (src.length <= 6) return src;
    const head = src.slice(0, 5);
    const tail = src.slice(5);
    return [
      ...head,
      {
        key: "Otros",
        count:  tail.reduce((a, r) => a + (r.count  || 0), 0),
        amount: tail.reduce((a, r) => a + (r.amount || 0), 0),
      },
    ];
  })();

  return (
    <TooltipProvider>
      <div ref={pageRef} className="p-6 md:p-8" data-testid="scorecard-page">
        <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="kpi-icon-clean" style={{
              width: 44, height: 44, color: "#f4c400",
              boxShadow: "inset 0 0 0 1px #f4c40033",
            }}>
              <Gauge size={22} />
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-extrabold"
                style={{ letterSpacing: "-0.02em" }}>
                Scorecard Negocios Digitales
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Gerencia Senior Comercial Negocios Digitales · Período: <strong>{data?.period_label || "—"}</strong>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end" data-pdf-hide>
            <button
              type="button"
              onClick={() => setCompareOpen(true)}
              data-testid="scorecard-compare-btn"
              title="Compara 2 o 3 campañas side-by-side (leads, cierre, ticket, sucursales)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-extrabold transition-all hover:shadow-sm"
              style={{ borderColor: "#0f172a", background: "#0f172a", color: "#f4c400" }}>
              <GitCompare size={13}/> Comparar campañas
            </button>
            <PdfExportButton
              targetRef={pageRef}
              filename="scorecard-negocios-digitales"
              title="SDM · Scorecard Negocios Digitales"
              subtitle={data?.period_label || ""}
              testid="scorecard-pdf-btn"
            />
            <Link to="/scorecard/metodologia"
              data-testid="scorecard-methodology-link"
              title="Ver la fórmula, fuente y estado de automatización de cada KPI"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-all hover:shadow-sm"
              style={{ borderColor: "#f4c40055", background: "#f4c40014", color: "#a16207" }}>
              <BookOpen size={13}/> Metodología
            </Link>
            <CacheBustButton role={user?.role}/>
            <DataFreshnessBadge />
          </div>
        </header>

        <FilterBar
          period={period} setPeriod={setPeriod}
          dateRange={dateRange} setDateRange={setDateRange}
          filters={filters} setFilters={setFilters}
          options={options}
          onResetAll={resetAll}
        />

        {loading && !data && <ScorecardSkeleton />}
        {showRefetchPill && data && <RefetchPill />}

        {data && (
          <>
            <OverviewRibbon o={data.overview} />
            <TicketPromedioMini rows={data.overview?.avg_ticket_by_unit} />

            {/* Active channel filter badge */}
            {selectedChannel && (
              <div className="flex items-center justify-between mb-4 p-3 rounded-lg"
                style={{ background: "#fef9c3", border: "1px solid #fde047" }}
                data-testid="channel-filter-badge">
                <div className="flex items-center gap-2 text-xs font-bold" style={{ color: "#854d0e" }}>
                  <span className="uppercase tracking-wider text-[10px]">Canal seleccionado:</span>
                  <span className="pill" style={{
                    background: "#f4c400", color: "#111827", padding: "3px 10px", fontSize: 11,
                  }}>
                    {selectedChannel}
                  </span>
                  <span className="text-[10px] font-semibold text-gray-600">
                    Timeseries + campañas restringidos a este canal
                  </span>
                </div>
                <button onClick={() => setSelectedChannel(null)}
                  className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md"
                  style={{ background: "#fff", border: "1px solid #d4d4d8", color: "#374151", cursor: "pointer" }}
                  data-testid="clear-channel-filter">
                  <X size={12} /> Limpiar
                </button>
              </div>
            )}

            {/* Row 1 — three pies side-by-side (stack on mobile / tablet) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
              <ChannelPieChart
                data={data.channels_conversion || []}
                enrichedByChannel={data.data_sources?.enriched_by_channel || {}}
                selectedChannel={selectedChannel}
                onSelectChannel={(ch) => setSelectedChannel((prev) => prev === ch ? null : ch)}
              />
              <SalesPieChart
                title="Ventas por Sucursal"
                subtitle="Distribución de unidades vendidas por showroom"
                data={data.sales_by_showroom || []}
                testId="sales-by-showroom"
              />
              <SalesPieChart
                title="Ventas por Modelo"
                subtitle="Top modelos por unidades vendidas"
                data={modelPieData}
                testId="sales-by-model"
              />
            </div>

            {/* Row 2 — Timeseries (full-width) */}
            <TimeseriesChart ts={data.timeseries} channelFilter={selectedChannel} />

            {/* Row 3 — SLA de Gestión de Leads (BDC + Piso side-by-side) */}
            <section className="mt-6" data-testid="sla-gestion-leads-section">
              <header className="mb-3">
                <h2 className="text-lg font-extrabold tracking-tight text-gray-800"
                  style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.01em" }}>
                  SLA de Gestión de Leads
                </h2>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Tiempos de respuesta y ranking — Primer Tiempo (BDC) + Gestión (Piso)
                </p>
              </header>
              <ResponseTimeCard filters={(() => {
                // Traducción de `period` → rango de fechas para que el
                // % Contactado se calcule sobre la ventana temporal exacta
                // que el resto del Scorecard usa (Este mes / Trimestre /
                // YTD / Rango custom). Sin este mapeo el card mostraba YTD
                // aunque el usuario hubiera pickeado "Este mes".
                const today = new Date();
                const yr    = today.getFullYear();
                let dateFrom, dateTo;
                if (period === "custom") {
                  dateFrom = dateRange.start;
                  dateTo   = dateRange.end;
                } else if (period === "current_month") {
                  const m = String(today.getMonth() + 1).padStart(2, "0");
                  dateFrom = `${yr}-${m}-01`;
                  dateTo   = today.toISOString().slice(0, 10);
                } else if (period === "current_quarter") {
                  const qStart = Math.floor(today.getMonth() / 3) * 3;
                  const m = String(qStart + 1).padStart(2, "0");
                  dateFrom = `${yr}-${m}-01`;
                  dateTo   = today.toISOString().slice(0, 10);
                } else { // year_to_date
                  dateFrom = `${yr}-01-01`;
                  dateTo   = today.toISOString().slice(0, 10);
                }
                // `selectedChannel` (drill-down desde pie) tiene precedencia
                // sobre el filtro múltiple de canal del FilterBar — así
                // mantenemos consistencia con el resto del Scorecard.
                const channelStr = selectedChannel
                  ? selectedChannel
                  : (filters.channel?.length ? filters.channel.join(",") : "Todos");
                return {
                  date_from: dateFrom,
                  date_to:   dateTo,
                  brand:         filters.brand?.length         ? filters.brand.join(",")         : "Todas",
                  branch:        filters.branch?.length        ? filters.branch.join(",")        : "Todas",
                  model:         filters.model?.length         ? filters.model.join(",")         : "Todos",
                  channel:       channelStr,
                  business_unit: filters.business_unit?.length ? filters.business_unit.join(",") : "Todas",
                };
              })()}/>
            </section>

            {/* Row 4 — Composite scorecard KPIs (Conversion / Attribution / Digital Property) */}
            <div className="mt-6 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
              <ConversionScoreCard
                dateFrom={(() => {
                  const today = new Date();
                  const yr = today.getFullYear();
                  if (period === "custom")          return dateRange.start;
                  if (period === "current_month")   { const m = String(today.getMonth()+1).padStart(2,"0"); return `${yr}-${m}-01`; }
                  if (period === "current_quarter") { const q = Math.floor(today.getMonth()/3)*3; return `${yr}-${String(q+1).padStart(2,"0")}-01`; }
                  return `${yr}-01-01`;
                })()}
                dateTo={period === "custom" ? dateRange.end : new Date().toISOString().slice(0,10)}
                unit={filters.business_unit?.length === 1 ? filters.business_unit[0] : null}
              />
              <AttributionRateCard
                period={period === "year_to_date" ? "YTD" : period}
                dateRange={dateRange}
                businessUnit={filters.business_unit?.length === 1 ? filters.business_unit[0] : null}
              />
              <DigitalPropertyScoreCard
                unit={filters.business_unit?.length === 1 ? filters.business_unit[0] : null}
                dateFrom={(() => {
                  const today = new Date();
                  const yr = today.getFullYear();
                  if (period === "custom")          return dateRange.start;
                  if (period === "current_month")   { const m = String(today.getMonth()+1).padStart(2,"0"); return `${yr}-${m}-01`; }
                  if (period === "current_quarter") { const q = Math.floor(today.getMonth()/3)*3; return `${yr}-${String(q+1).padStart(2,"0")}-01`; }
                  return `${yr}-01-01`;
                })()}
                dateTo={period === "custom" ? dateRange.end : new Date().toISOString().slice(0,10)}
              />
            </div>

            {/* Row 5 — Web sub-sources breakdown (only when Web is in scope) */}
            {data.web_subsources && data.web_subsources.length > 0 && (
              <div className="mt-6">
                <WebSubsourcesPanel data={data.web_subsources} />
              </div>
            )}

            {/* Row 6 — Attribution audit (full-width · collapsable · lazy-loaded on open) */}
            <div className="mt-6">
              <AttributionAuditSection period={period} filters={filters} dateRange={dateRange} />
            </div>

            {/* Row 7 — Top Campañas por Conversión (moved to the bottom + collapsable) */}
            <details className="mt-6 surface" data-testid="campaigns-collapse">
              <summary
                className="cursor-pointer px-4 py-3 flex items-center justify-between select-none"
                style={{ borderRadius: 8 }}
                data-testid="campaigns-collapse-toggle">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">
                    Top Campañas por Conversión
                  </span>
                  <span className="pill" style={{
                    background: "#f1f5f9", color: "#475569",
                    padding: "2px 8px", fontSize: 10, borderRadius: 999,
                  }}>
                    {filteredCampaigns?.length || 0} campañas
                  </span>
                </div>
                <ChevronDown size={16} className="text-gray-500 transition-transform"
                  style={{ transform: "rotate(0deg)" }} />
              </summary>
              <div className="px-4 pb-4 pt-2">
                <CampaignTable data={filteredCampaigns} filterBadge={selectedChannel} embedded />
              </div>
            </details>
          </>
        )}

        <footer className="mt-6 p-4 rounded-lg"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <div className="flex items-start gap-2">
            <Info size={14} style={{ color: "#64748b", flexShrink: 0, marginTop: 2 }} />
            <div className="text-[11px] text-gray-600" style={{ lineHeight: 1.6 }}>
              <strong>Alcance MVP:</strong> KPIs revenue-focused con data ya disponible. Sales attainment mide unidades vendidas vs plan (PVA en unidades — convención automotriz). Lead-to-Sale usa matching compuesto: email O nombre+apellido normalizados (accent + case-insensitive) O teléfono últimos 10 dígitos. Baseline: Julio 2026. <strong>Distribuidor</strong> incluye leads del canal <em>Red de Dealers Externos</em> y ventas con tipo de compra <em>Dealer</em> o <em>F. Externo</em>.
            </div>
          </div>
        </footer>

        <CampaignCompareModal
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          allCampaigns={options.campaigns || []}
          period={period}
          dateRange={dateRange}
          year={2026}
        />
      </div>
    </TooltipProvider>
  );
}

